import { IExchange } from '@efex-services/rate/types';
import { RateValidationResult } from '../components/RateValidationAlert';
import {
  MINIMAL_ADJUSTMENT,
  RATE_ROUNDING_DECIMALS,
  RATE_ROUNDING_MULTIPLIER,
  RATE_VALIDATION_COMPARISON_KEYS,
} from '../constants/catalogs';

export const applyEfexRateRounding = (
  rate: number,
  isMainCurrency: boolean
): number => {
  const multiplier = RATE_ROUNDING_MULTIPLIER;

  return isMainCurrency
    ? Math.floor(rate * multiplier) / multiplier
    : Math.ceil(rate * multiplier) / multiplier;
};

export const formatEfexRate = (
  rate: number,
  isMainCurrency: boolean
): string => {
  const roundedRate = applyEfexRateRounding(rate, isMainCurrency);
  return roundedRate.toFixed(RATE_ROUNDING_DECIMALS);
};

export const getValidPromoterRate = (
  exchangeData: IExchange | null
): string => {
  if (!exchangeData?.buy) return '0';

  const baseRate = parseFloat(exchangeData.buy);
  const { isMainCurrency } = exchangeData;

  const adjustedRate = isMainCurrency
    ? baseRate - MINIMAL_ADJUSTMENT
    : baseRate + MINIMAL_ADJUSTMENT;

  return formatEfexRate(adjustedRate, isMainCurrency);
};

export const getBaseRateForValidation = (
  exchangeData: IExchange | null
): number => {
  if (!exchangeData) return 0;

  const baseRate = parseFloat(exchangeData.buy || '0');

  return applyEfexRateRounding(baseRate, exchangeData.isMainCurrency);
};

export const validatePromoterRate = (
  rate: number,
  exchangeData: IExchange | null
): RateValidationResult => {
  if (!exchangeData || rate < 0) {
    return {
      isValid: false,
      messageKey: 'error',
      messageParams: { comparison: '', rate: '0' },
    };
  }

  const { isMainCurrency } = exchangeData;

  const roundedInputRate = applyEfexRateRounding(rate, isMainCurrency);
  const roundedBaseRate = getBaseRateForValidation(exchangeData);

  const comparisonKey = isMainCurrency
    ? RATE_VALIDATION_COMPARISON_KEYS.LOWER
    : RATE_VALIDATION_COMPARISON_KEYS.GREATER;

  const isValid = isMainCurrency
    ? roundedInputRate < roundedBaseRate && roundedInputRate > 0
    : roundedInputRate > roundedBaseRate;

  return {
    isValid,
    messageKey: 'rateMustBe',
    messageParams: {
      comparison: comparisonKey,
      rate: roundedBaseRate.toFixed(RATE_ROUNDING_DECIMALS),
    },
  };
};

export const parseValidRate = (
  rateString: string | number | null | undefined,
  exchangeData?: IExchange | null
): number | null => {
  if (rateString == null) return null;

  const stringValue =
    typeof rateString === 'string' ? rateString : String(rateString);

  if (!stringValue.trim()) return null;

  const rate = parseFloat(stringValue);
  if (Number.isNaN(rate) || rate <= 0) return null;

  if (exchangeData) {
    return applyEfexRateRounding(rate, exchangeData.isMainCurrency);
  }

  return rate;
};

export const validateDecimalInput = (value: string): string => {
  let cleaned = value.replace(/[^\d.]/g, '');

  const parts = cleaned.split('.');
  if (parts.length > 2) {
    cleaned = `${parts[0]}.${parts.slice(1).join('')}`;
  }

  if (parts.length === 2 && parts[1].length > RATE_ROUNDING_DECIMALS) {
    cleaned = `${parts[0]}.${parts[1].substring(0, RATE_ROUNDING_DECIMALS)}`;
  }

  return cleaned;
};
